import React, { useState, useCallback } from 'react';
import { useQuery } from '@apollo/client';
import { Box, Flex } from '@atlaskit/primitives';
import Button from '@atlaskit/button';
import Lozenge from '@atlaskit/lozenge';
import { setDefaultPaymentMethod } from '@atlassian/temp-proxies';
import {
  AchPanel,
  MoreActions,
  MoreActionsItem,
  useSidebar,
  WarningDialog,
  useNotification,
  NotificationTypes,
  errorNotificationBuilder,
  successNotificationBuilder,
  OpenSidebarPayload,
} from '@css-xp/base';
import { ADVOCATE_ACCESS_TYPE, ADVOCATE_RESOURCE_TYPE, useAdvocateAccessControl } from '@css-xp/ppc-access-control';
import { failureMessage } from '@css-xp/ppc-common';
import { Ach } from '@css-xp/graphtypes/src/generated-types-new';
import { fetchBillingProfilesByPaymentMethodIdQuery } from '@css-xp/billing-v2';
import { defaultBillingProfileSortOptions, maxThresholdForResults } from '@css-xp/ppc-constants';
import { container } from './ach-payment.styles';
import { CARD_PAYMENTS_SIDEBAR } from '../../../common/types';

type AchPaymentProps = {
  onUpdateDone?: () => void;
  txaId?: string;
  ach?: Ach | any;
};

export const AchPayment = ({ ach, onUpdateDone, txaId }: AchPaymentProps) => {
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const { openSidebar } = useSidebar();
  const { sendSuccessNotification, notificationSent, setNotificationSent, sendErrorNotification } = useNotification();

  const isPaymentUpdateEnabledForAdvocates = useAdvocateAccessControl({
    accessType: ADVOCATE_ACCESS_TYPE.UPDATE,
    resource: ADVOCATE_RESOURCE_TYPE.PAYMENT_METHOD,
  });
  const isPaymentDeletionEnabledForAdvocates = useAdvocateAccessControl({
    accessType: ADVOCATE_ACCESS_TYPE.DELETE,
    resource: ADVOCATE_RESOURCE_TYPE.PAYMENT_METHOD,
  });

  const { data, loading, error } = useQuery(fetchBillingProfilesByPaymentMethodIdQuery, {
    variables: {
      id: ach.id,
      skip: !ach.id,
      searchOptions: {
        first: maxThresholdForResults, // Disclaimer: Max threshold for the query
        offset: 0,
        sort: defaultBillingProfileSortOptions,
      },
    },
  });
  const billingProfiles = data?.billingProfilesByPaymentMethodId?.billingProfiles || [];

  const handleSidebarAction = (openSidebarPayload: OpenSidebarPayload) => {
    openSidebar(openSidebarPayload);
  };

  const handleSetAsDefaultClick = async () => {
    if (ach && ach.id) {
      setIsUpdating(true);
      try {
        await setDefaultPaymentMethod({
          transactionAccountId: txaId as string,
          id: ach.id,
        });
        if (!notificationSent) {
          const notificationOptions = successNotificationBuilder()
            .isVersion(NotificationTypes.FLAG)
            .withId('update-default-payment-success')
            .withHeader('Update successful')
            .withMessage(`Ach account set as default payment method.`)
            .withDismissAfter(5000)
            .build();
          sendSuccessNotification(notificationOptions);
          setNotificationSent(true);
        }
        setIsConfirmDialogOpen(false);
        onUpdateDone?.();
      } catch (err) {
        if (!notificationSent) {
          const errorNotificationOptions = errorNotificationBuilder()
            .isVersion(NotificationTypes.FLAG)
            .withHeader('Update unsuccessful')
            .withMessage(<>We're unable to set the payment method as default - {failureMessage()}</>)
            .withId('update-default-payment-error')
            .withDismissAfter(5000)
            .build();
          sendErrorNotification(errorNotificationOptions);
          setNotificationSent(true);
        }
      } finally {
        setIsUpdating(false);
      }
    }
  };

  const paymentMethodActions: MoreActionsItem[] = [
    ...(isPaymentDeletionEnabledForAdvocates
      ? [
          {
            id: 'View associated billing profiles',
            content: 'View associated billing profiles',
            action: () =>
              handleSidebarAction({
                sidebarType: CARD_PAYMENTS_SIDEBAR.VIEW_BILLING_PROFILE,
                payload: {
                  paymentMethod: ach,
                  billingProfiles: billingProfiles,
                  isLoading: loading,
                  transactionAccountId: txaId,
                  isFromBillingProfile: true,
                  error: error,
                },
                sidebarOptions: {
                  sidebarWidth: '600px',
                },
              }),
          },
          {
            id: 'Remove',
            content: 'Remove',
            action: () =>
              handleSidebarAction({
                sidebarType: CARD_PAYMENTS_SIDEBAR.REMOVE_PAYMENT_METHOD,
                payload: {
                  refetch: onUpdateDone,
                  paymentMethod: ach,
                  transactionAccountId: txaId,
                  version: 2,
                  isACHPaymentMethod: true,
                },
              }),
          },
        ]
      : []),
  ];

  const renderMoreActions = () => (
    <MoreActions actions={paymentMethodActions} testId="ach-payment-method-actions-btn" />
  );

  const getDescription = useCallback(() => {
    return (
      <div className="payment-warning-description">
        <p className="pt-8">
          The new default payment method will be the preferred payment method for future purchases.
        </p>
        <p className="pt-8">If you need to change it for current subscriptions, go to the billing profile page.</p>
      </div>
    );
  }, []);

  const renderWarningDialog = () => {
    return (
      <WarningDialog
        isOpen={isConfirmDialogOpen}
        onClose={() => setIsConfirmDialogOpen(false)}
        onConfirm={() => {
          handleSetAsDefaultClick();
        }}
        header={'This will be used for future purchases'}
        description={getDescription()}
        okLabel={'Confirm'}
        cancelLabel={'Close'}
        isLoading={isUpdating}
      />
    );
  };

  return (
    <Box xcss={container} padding="space.200" testId="ach-payment-method">
      <Flex justifyContent="space-between" alignItems="center">
        <Flex>
          <AchPanel bankName={ach.ach?.bankName} last4={ach.ach?.last4} iconHeight={25} iconWidth={45} />
        </Flex>
        <Flex gap="space.200" alignItems="center">
          {ach.default && <Lozenge>Default</Lozenge>}
          {!ach.default && isPaymentUpdateEnabledForAdvocates && (
            <Box paddingInlineStart="space.200">
              <Button
                appearance="link"
                onClick={() => setIsConfirmDialogOpen(true)}
                testId="set-as-default-payment-method-btn"
              >
                Set as default
              </Button>
            </Box>
          )}
          {renderMoreActions()}
        </Flex>
        {renderWarningDialog()}
      </Flex>
    </Box>
  );
};
